import {fillArray} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {noop} from 'jquery';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {ProcedureExperimentRowLabel} from 'procedures/planning/ProcedureExperimentRowLabel';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {ProcedureRunConditionsSummary} from 'studies/typed/shared/procedure-runs/conditions/ProcedureRunConditionsSummary';
import {
  BaseCondition,
  ConditionValidationState,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

import {
  buildConditionGroupsFromTableUpdate,
  buildConditionsFromTableUpdate,
  confirmDeleteConditions,
  dispatchByConditionShape,
  getDescErrorTriangle,
  getNameErrorTriangle,
  makeValueCell,
  resolveConditionName,
  resolveReplicateCount,
} from './replicateTableUtils';

interface PDColumnConstraint extends ColumnConstraint {
  processDevelopmentCustomIndex: number;
}

function makeInitialReplicateTable(
  tableName: string,
  replicatesPerCondition: Array<number>,
  namesPerCondition: Array<string>,
  descriptionsPerCondition: Array<string | undefined>,
  validationStatePerCondition: Array<ConditionValidationState>,
  isLockedPerCondition: Array<boolean>,
  locked: boolean,
  procedureType: ProcedureType
): Table {
  const makeNameCell = (text: string, index: number): Cell => ({
    text,
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    bold: false,
    userAnnotationIndicator: getNameErrorTriangle(validationStatePerCondition, index, procedureType),
  });

  const makeDescCell = (text: string, index: number): Cell => ({
    text,
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    bold: false,
    userAnnotationIndicator: getDescErrorTriangle(validationStatePerCondition, index, procedureType),
  });

  const numConditions = replicatesPerCondition.length;
  return {
    name: tableName,
    _firstColumnWidth: 170,
    widths: replicatesPerCondition.map(() => 120),
    rowLabels: [
      <ProcedureExperimentRowLabel
        name={`${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} name`}
      />,
      <ProcedureExperimentRowLabel name="Description" />,
      <ProcedureExperimentRowLabel name="Number of replicates" />,
    ],
    columnLabels: fillArray(
      numConditions,
      (conditionNumber) =>
        `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${
          conditionNumber + 1
        }`
    ),
    columnConstraints: replicatesPerCondition.map((_numReplicates, conditionIdx): PDColumnConstraint => ({
      processDevelopmentCustomIndex: conditionIdx,
      editLabelDisabled: true,
    })),
    headerCol: false,
    headerRow: false,
    table: [
      fillArray(numConditions, (conditionIndex) =>
        makeNameCell(namesPerCondition[conditionIndex], conditionIndex)
      ),
      fillArray(numConditions, (conditionIndex) =>
        makeDescCell(descriptionsPerCondition[conditionIndex] ?? '', conditionIndex)
      ),
      fillArray(numConditions, (conditionIndex) =>
        makeValueCell(replicatesPerCondition[conditionIndex], conditionIndex, locked, isLockedPerCondition)
      ),
    ],
    lockedDims: {allowAddRows: false, allowRemoveRows: false, allowEditCols: !locked},
    allowColumnEdits: !locked,
    _hideRename: true,
  };
}

/**
 *  Update conditions when any individual cell is updated.
 *
 *  @package exported for testing.
 */
export function updateTableCells<TCondition extends BaseCondition>(
  table: Table,
  conditions: Array<TCondition>,
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void,
  procedureType: ProcedureType
): void {
  const originalValueIndexes = table.columnConstraints.map(
    (c) => (c as PDColumnConstraint)?.processDevelopmentCustomIndex ?? null
  );

  // assigns newly added column cells (which have a originalValueIndex == null) to have a cell.text = '1'
  const newConditions = table.table[2].map((cell, index) => {
    const originalIndex = originalValueIndexes[index];
    const isNewColumn = originalIndex == null;
    const originalCondition = originalIndex == null ? undefined : conditions[originalIndex];
    const numReplicatesCell = {...cell, text: isNewColumn ? '1' : cell.text};
    return {
      numReplicates: resolveReplicateCount(numReplicatesCell, isNewColumn, originalCondition),
      name: resolveConditionName(table.table[0][index].text, index, procedureType),
      description: table.table[1][index].text,
    };
  });
  const updated = dispatchByConditionShape(
    conditions,
    (cs) => buildConditionsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs}),
    (cs) => buildConditionGroupsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs})
  );
  setConditions(updated as unknown as Array<TCondition>, originalValueIndexes);
}

export function ReplicateTable<TCondition extends BaseCondition>(props: {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editNumColsButtons: JSX.Element;
  locked: boolean;
  estimate: TimingEstimateResult;
  procedureType: ProcedureType;
  isPlan?: boolean;
  dataTestAttrs?: {[key: string]: string};
}): JSX.Element {
  const isPlan = props.isPlan;
  const table = makeInitialReplicateTable(
    `${getProcedureRunConditionTextForProcedureType(props.procedureType, {capital: true})} settings`,
    props.conditions.map((item) => item.numReplicates),
    props.conditions.map((item) => item.name),
    props.conditions.map((item) => item.description),
    props.conditions.map(
      (item) =>
        item.validationState ?? {
          isDuplicateName: false,
          isNameLengthOverLimit: false,
          isDescLengthOverLimit: false,
        }
    ),
    // Persisted conditions are locked. No conditions are locked for procedure run plans.
    isPlan ? props.conditions.map(() => false) : props.conditions.map((item) => isPersistedCondition(item)),
    props.locked,
    props.procedureType
  );

  const conditionsSummary = (
    <ProcedureRunConditionsSummary
      conditions={props.conditions}
      estimate={props.estimate}
      procedureType={props.procedureType}
    />
  );

  const leftHeaderControls = !props.locked ? props.editNumColsButtons : undefined;

  function handleDeleteColumns(
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void,
    isProcedureRunPlan: boolean
  ): void {
    const conditionsToRemove = props.conditions.slice(start, end + 1);
    confirmDeleteConditions({
      unitWord: 'column',
      conditionsToRemove,
      experimentalInputs: props.experimentalInputs,
      procedureType: props.procedureType,
      isProcedureRunPlan,
      doDelete: () => {
        doDelete(index, start, end);
      },
    });
  }

  return (
    <div className="u-flex-display mediocre">
      <div className={css(styles.maxWidth)}>
        <SimpleTableEditable
          {...standaloneProps}
          dataTestAttrs={props.dataTestAttrs}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          isLeftAlignRowHeader={true}
          locked={props.locked}
          table={table}
          cid="doe-replicates"
          updateHandler={(_, {table}: {table: Table}) => {
            updateTableCells(table, props.conditions, props.setConditions, props.procedureType);
          }}
          storeState={noop}
          restoreState={noop}
          isCollapsible={true}
          leftHeaderControls={leftHeaderControls}
          headerControls={conditionsSummary}
          hideRowCount={true}
          onDeleteColumns={(index, start, end, doDelete) => {
            handleDeleteColumns(index, start, end, doDelete, isPlan ?? false);
          }}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({maxWidth: {width: '100%', paddingRight: '24px'}});
